import moment from 'moment';
import { adviserFactsheetOf } from '../../../utils/client-factsheet';
import type { ClientFactsheet, ClientTag, QueueClient } from '../../../types/client';

export const UNSPECIFIED_GENDER = '__unspecified__';
export const UNSET_PERSONALITY = '__unset__';

export type BirthdayFilter = 'all' | 'this-month' | 'next-1' | 'next-2' | 'next-3' | 'month';
export type LastMeetingFilter = 0 | 1 | 3 | 6 | 12;

/**
 * The five policy categories the server already summarises per client
 * (server/utils/client.js:378-393). No backend change was needed for these —
 * `policyDataReducer` merges the counts onto every formatted client, and
 * `getClientById` recomputes them from an identical projection, so a
 * single-client refresh cannot make them drift.
 */
export const POLICY_CATEGORIES = ['life', 'ci', 'medical', 'saving', 'ilas'] as const;
export type PolicyCategory = (typeof POLICY_CATEGORIES)[number];

export type ClientFilters = {
  importance: string[];
  potential: number[];
  birthday: BirthdayFilter;
  birthdayMonth: number;
  lastMeetingMonths: LastMeetingFilter;
  gender: string[];
  ageMin: number;
  ageMax: number;
  existingClient: string[];
  source: string[];
  referrerName: string;
  personality: string[];
  lifeCoverage: number;
  ciCoverage: number;
  monthlyIncome: number;
  monthlySaving: number;
  families: string[];
  tags: string[];
  /** Categories the client must hold at least one policy in. */
  policyHoldings: PolicyCategory[];
  /** Categories the client must hold NO policy in — the coverage-gap direction. */
  policyGaps: PolicyCategory[];
};

export const createDefaultClientFilters = (): ClientFilters => ({
  importance: [],
  potential: [],
  birthday: 'all',
  birthdayMonth: 0,
  lastMeetingMonths: 0,
  gender: [],
  ageMin: 0,
  ageMax: 100,
  existingClient: [],
  source: [],
  referrerName: '',
  personality: [],
  lifeCoverage: 0,
  ciCoverage: 0,
  monthlyIncome: 0,
  monthlySaving: 0,
  families: [],
  tags: [],
  policyHoldings: [],
  policyGaps: []
});

/**
 * Whether any sidebar filter is currently narrowing the list. Derived from
 * `createDefaultClientFilters` rather than a hand-written field list, so a
 * filter added to `ClientFilters` counts here without a second edit: every
 * default array is the canonical 「全部」 empty, and every default scalar
 * compares by value.
 */
export const hasActiveClientFilters = (filters: ClientFilters): boolean =>
  (Object.entries(createDefaultClientFilters()) as [keyof ClientFilters, unknown][]).some(([key, fallback]) => {
    const value: unknown = filters[key];
    return Array.isArray(value) ? value.length > 0 : value !== fallback;
  });

/**
 * Mirrors searchFilter.js editStateArray without mutating React state.
 * An empty array is the canonical 「全部」 state: choosing an option removes
 * 「全部」, and removing the final option naturally returns the group to it.
 */
export const editFilterArray = <T>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter(item => item !== value) : [...values, value];

/**
 * Keep an active family filter valid after the adviser renames or deletes a
 * family through the shared EditFamilyDialog. This mirrors the legacy
 * clientPage.refreshData/applySearchFilter flow without mutating filter state.
 */
export const migrateFamilyFilter = (
  filters: ClientFilters,
  oldFamilyName: string,
  newFamilyName?: string
): ClientFilters => {
  if (!filters.families.includes(oldFamilyName)) return filters;

  const families = newFamilyName
    ? Array.from(new Set(filters.families.map(family => (family === oldFamilyName ? newFamilyName : family))))
    : filters.families.filter(family => family !== oldFamilyName);

  return { ...filters, families };
};

const numberOf = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Does this client hold at least one policy in the category?
 *
 * Four of the five read their own subcategory-gated count. 危疾 deliberately
 * reads `ciCoverage` instead of `ciPolicyCount`: that count's branch tests only
 * that `sumInsured` has a value, and AddPolicy's `formatPaymentData` writes
 * `sumInsured: 0` on every policy it saves, so the server increments it for
 * policies of every category. `ciCoverage` sums the same field and is therefore
 * correct — a non-CI policy contributes zero.
 * See docs/specs/2026-08-03-clientv2-policy-filters-findings.md §3.
 *
 * A client whose policy data the server withholds (已中斷 — the `dataAccess`
 * gate) has every scalar absent, so they read as a gap in all five. That
 * matches how the coverage and monthly-saving filters above already treat them.
 */
const holdsPolicyCategory = (client: QueueClient, category: PolicyCategory): boolean => {
  if (category === 'ci') return numberOf(client.ciCoverage) > 0;
  if (category === 'medical') {
    const medical = client.medicalPolicyCounts;
    return numberOf(medical?.highend) + numberOf(medical?.others) > 0;
  }
  const counts: Record<'life' | 'saving' | 'ilas', unknown> = {
    life: client.lifePolicyCount,
    saving: client.savingPolicyCount,
    ilas: client.ilasPolicyCount
  };
  return numberOf(counts[category]) > 0;
};

// NOTE: there was a `hasAnyPolicy` here, OR-ing the five categories above into
// "does this client hold any policy at all", to route the 救命紙 card. Five
// categories cannot answer that question — `POLICY_CATEGORIES` covers 5 of the
// 12 subcategories in constants/insuranceCategories.js, and every scalar is
// withheld for 已中斷 — so it is gone and the card has one destination again.
// The predicates in this file answer "is there a gap in category X", which is
// the only question the aggregates can support; keep it that way.

/**
 * Order the drawer names a cross-sell gap in. Protection before accumulation,
 * because those are the two an adviser can raise without a fresh needs
 * conversation. ILAS is deliberately absent: not holding one is not a gap.
 */
const CROSS_SELL_ORDER: PolicyCategory[] = ['ci', 'medical', 'life', 'saving'];

/**
 * The category to raise with a client who has already bought something — or
 * null when there is nothing honest to say.
 *
 * Two CATEGORY-level facts, which is exactly what the note above says these
 * aggregates can support: this client holds at least one of the five tracked
 * categories, and is missing at least one. It never claims "has no policies" —
 * a client whose only cover is one of the seven untracked subcategories reads
 * as holding nothing here, and the caller's answer to that is silence, not a
 * wrong statement.
 *
 * The holding gate is the point. A client with none of the five is a prospect,
 * and telling an adviser their prospect has no insurance is not news; a client
 * who bought life and never bought critical illness is a conversation, and one
 * the adviser cannot have without opening the portfolio to notice it.
 */
export const crossSellGapOf = (client: QueueClient): PolicyCategory | null => {
  if (!holdsAnyTrackedCategory(client)) return null;
  return CROSS_SELL_ORDER.find(category => !holdsPolicyCategory(client, category)) || null;
};

/**
 * Holds at least one of the FIVE TRACKED categories.
 *
 * Read the name literally. This is not "has any policy" — the note above
 * explains why these aggregates cannot answer that, and why a `hasAnyPolicy`
 * built from them was removed. A client whose only cover is one of the seven
 * untracked subcategories answers false here, so anything downstream must
 * treat false as "nothing to say" and never as "this client is uninsured".
 */
export const holdsAnyTrackedCategory = (client: QueueClient): boolean =>
  POLICY_CATEGORIES.some(category => holdsPolicyCategory(client, category));

/**
 * Whether the server sent this client's policy figures at all.
 *
 * `formatClientData` puts every policy aggregate behind `dataAccess`, so for a
 * 已中斷 client the scalars are absent because they are withheld, not because
 * they are zero. Every predicate above reads as "holds nothing" for them, which
 * is why callers have to ask this first.
 *
 * Same expression as `isRankable` in clientCpiGap.ts, and for the same
 * underlying reason (both mirror the server's `dataAccess`) — kept separate
 * because they answer different questions and nothing guarantees the C.P.I.
 * gate and the policy gate stay identical.
 */
export const policyFiguresVisible = (client: QueueClient): boolean =>
  Boolean(client.inactiveUser) || (client as { connect?: boolean }).connect === true;

const ageOf = (factsheet: ClientFactsheet, now: moment.Moment): number | null => {
  const storedAge = numberOf(factsheet.age);
  if (storedAge > 0) return storedAge;
  if (!factsheet.dob) return null;
  const dob = moment.parseZone(factsheet.dob);
  return dob.isValid() ? now.diff(dob, 'years') : null;
};

const nextBirthdayOf = (dobValue: string, now: moment.Moment): moment.Moment | null => {
  const dob = moment.parseZone(dobValue);
  if (!dob.isValid()) return null;
  const birthday = now.clone().month(dob.month()).date(dob.date()).startOf('day');
  if (birthday.isBefore(now.clone().startOf('day'))) birthday.add(1, 'year');
  return birthday;
};

const matchesBirthday = (
  factsheet: ClientFactsheet,
  client: QueueClient,
  filters: ClientFilters,
  now: moment.Moment
): boolean => {
  if (filters.birthday === 'all') return true;
  const dobValue = factsheet.dob || client.dob;
  if (!dobValue) return false;
  const dob = moment.parseZone(dobValue);
  if (!dob.isValid()) return false;
  if (filters.birthday === 'this-month') return dob.month() === now.month();
  if (filters.birthday === 'month') return filters.birthdayMonth > 0 && dob.month() + 1 === filters.birthdayMonth;

  const months = Number.parseInt(filters.birthday.slice(-1), 10);
  const nextBirthday = nextBirthdayOf(dobValue, now);
  return Boolean(nextBirthday && !nextBirthday.isAfter(now.clone().add(months, 'months').endOf('day')));
};

const matchesLastMeeting = (
  factsheet: ClientFactsheet,
  thresholdMonths: LastMeetingFilter,
  now: moment.Moment
): boolean => {
  if (thresholdMonths === 0) return true;
  if (!factsheet.latestMeetingDate) return false;
  const meeting = moment(factsheet.latestMeetingDate);
  return meeting.isValid() && meeting.isBefore(now.clone().subtract(thresholdMonths, 'months'));
};

const hasFactsheetFilter = (filters: ClientFilters): boolean =>
  filters.importance.length > 0 ||
  filters.birthday !== 'all' ||
  filters.lastMeetingMonths > 0 ||
  filters.ageMin > 0 ||
  filters.ageMax < 100 ||
  filters.existingClient.length > 0 ||
  filters.source.length > 0 ||
  filters.referrerName.trim().length > 0 ||
  filters.personality.length > 0 ||
  filters.monthlyIncome > 0 ||
  filters.families.length > 0 ||
  filters.tags.length > 0;

const matchesFactsheetFilters = (
  client: QueueClient,
  factsheet: ClientFactsheet,
  filters: ClientFilters,
  now: moment.Moment
): boolean => {
  if (filters.importance.length > 0 && !filters.importance.includes(factsheet.importance || '')) return false;
  if (!matchesBirthday(factsheet, client, filters, now)) return false;
  if (!matchesLastMeeting(factsheet, filters.lastMeetingMonths, now)) return false;

  if (filters.ageMin > 0 || filters.ageMax < 100) {
    const age = ageOf(factsheet, now);
    if (age === null || age < filters.ageMin || age > filters.ageMax) return false;
  }

  if (filters.existingClient.length > 0 && !filters.existingClient.includes(factsheet.existingClient || '')) {
    return false;
  }
  if (filters.source.length > 0 && !filters.source.includes(factsheet.source || '')) return false;

  const referrer = filters.referrerName.trim().toLocaleLowerCase();
  if (referrer && !(factsheet.referrerName || '').toLocaleLowerCase().includes(referrer)) return false;

  if (filters.personality.length > 0) {
    const personality = factsheet.personality || UNSET_PERSONALITY;
    if (!filters.personality.includes(personality)) return false;
  }

  if (filters.monthlyIncome > 0 && numberOf(factsheet.monthlyIncome) < filters.monthlyIncome) return false;

  if (filters.families.length > 0) {
    const families = factsheet.families || [];
    if (!families.some(family => filters.families.includes(family))) return false;
  }

  if (filters.tags.length > 0) {
    const tags = factsheet.tags || [];
    if (!tags.some(tag => filters.tags.includes(tag.text))) return false;
  }

  return true;
};

export const filterClients = (
  clients: QueueClient[],
  filters: ClientFilters,
  input: { adviserEmail?: string; now?: moment.MomentInput } = {}
): QueueClient[] => {
  const now = moment(input.now);
  return clients.filter(client => {
    const factsheet = adviserFactsheetOf(client, input.adviserEmail);

    if (filters.potential.length > 0) {
      const level = Number.parseInt(String(client.scoreLevel), 10);
      if (!client.validScore || !filters.potential.includes(level)) return false;
    }

    if (filters.gender.length > 0) {
      const gender = factsheet?.gender || client.gender || UNSPECIFIED_GENDER;
      if (!filters.gender.includes(gender)) return false;
    }

    // These four values are policy-derived top-level fields, not factsheet
    // reads. The threshold directions deliberately mirror clientPage.js:
    // coverage/monthly saving are "at or below", monthly income is "at least".
    if (filters.lifeCoverage > 0) {
      const value = numberOf(client.lifeCoverage);
      if (value <= 0 || value > filters.lifeCoverage) return false;
    }
    if (filters.ciCoverage > 0) {
      const value = numberOf(client.ciCoverage);
      if (value <= 0 || value > filters.ciCoverage) return false;
    }
    if (
      filters.monthlySaving > 0 &&
      numberOf(client.totalPremiumSaving) + numberOf(client.totalWithILAS) > filters.monthlySaving
    ) {
      return false;
    }

    // Policy categories belong in this block, not with the factsheet filters
    // below: they read top-level scalars, so a client with no factsheet at all
    // must still be judged on them rather than falling through to
    // `hasFactsheetFilter`. OR inside each group, AND between the two.
    if (filters.policyHoldings.length > 0 && !filters.policyHoldings.some(c => holdsPolicyCategory(client, c))) {
      return false;
    }
    if (filters.policyGaps.length > 0 && !filters.policyGaps.some(c => !holdsPolicyCategory(client, c))) {
      return false;
    }

    if (!factsheet) return !hasFactsheetFilter(filters);
    return matchesFactsheetFilters(client, factsheet, filters, now);
  });
};

export type ClientFilterOptions = {
  families: string[];
  tags: ClientTag[];
  /**
   * Every referrer name the adviser has already typed on a factsheet, for the
   * 介紹人 field's suggestions. V1 keeps the same list in Redux
   * (`state.client.referrerNames`, built by UPDATE_CLIENT_REFERRER_NAMES from
   * `factsheetId.referrerName`) and feeds it to a freeSolo Autocomplete in both
   * the filter panel and the personal-details form. Derived here instead of
   * held in a duck, like families and tags above: the list is a pure function
   * of the clients already in hand, and a second copy in the store is a second
   * thing to keep in sync.
   */
  referrers: string[];
};

/** Build sidebar family/tag/referrer choices from the full array plus the adviser palette. */
export const clientFilterOptionsOf = (
  clients: QueueClient[],
  input: {
    adviserEmail?: string;
    families?: string[];
    usedTags?: ClientTag[];
  } = {}
): ClientFilterOptions => {
  const families = new Set(input.families || []);
  const tags = new Map<string, ClientTag>();
  // Case-insensitive de-dupe keyed on the trimmed lowercase name, keeping the
  // first spelling seen — 「Peter Chan」 and 「peter chan」 are one referrer, and
  // offering both as separate suggestions is how a filter that matches
  // case-insensitively (matchesFactsheetFilters) ends up looking broken.
  const referrers = new Map<string, string>();

  (input.usedTags || []).forEach(tag => {
    if (tag?.text) tags.set(tag.text, tag);
  });

  clients.forEach(client => {
    const factsheet = adviserFactsheetOf(client, input.adviserEmail);
    (factsheet?.families || []).forEach(family => families.add(family));
    (factsheet?.tags || []).forEach(tag => {
      if (tag?.text && !tags.has(tag.text)) tags.set(tag.text, tag);
    });
    const referrer = (factsheet?.referrerName || '').trim();
    const key = referrer.toLowerCase();
    if (referrer && !referrers.has(key)) referrers.set(key, referrer);
  });

  return {
    families: Array.from(families).sort((left, right) => left.localeCompare(right)),
    tags: Array.from(tags.values()).sort((left, right) => left.text.localeCompare(right.text)),
    referrers: Array.from(referrers.values()).sort((left, right) => left.localeCompare(right))
  };
};
